import axios from 'axios';
import { isStopped } from './stoppableCalls';
import { createDocEntryStore } from './docStore';
import { readState, writeState } from './docState';
import { renderPdfPageBand } from './pdfRender';
import { buildStepInstructionsPrompt } from './stepInstructionsPrompt';
import type { StepBand } from './categoryBands';
import type { InstrEntry } from './stepsRun';
import { haltGate } from './runHalt';
import { asPicture, withDoc } from './promptPictures';
import type { TipPicture } from './PromptBadge';

// WHAT EACH STEP PRINTS, transcribed: Phase 3's second call — ONE for the whole manual, run
// beside the repartition rather than after it (it needs nothing the repartition answers).
//
// It was one call per step, four in flight. A copy job does not get better by being asked a
// step at a time, and a page carrying two steps went up twice.
//
// It was a clause of Phase 4's per-step analysis before that — a fourth thing asked of the call
// that also has to break the step into atomic sub-steps, count what each one consumes and name
// the holes it enters. Which put a transcription behind a judgement, and every re-run of Phase 4
// re-typed words that are printed on paper and cannot have changed.
//
// Here it needs no context at all: no parts list, no earlier steps, no catalogue. The pages,
// which step is on which, and a reply with a ground truth on the page — the one answer in this
// app that can be checked by looking rather than argued about.

// step category ("Assemblage - Etape N") -> its printed text, per language.
export type ByInstr = Record<string, InstrEntry[]>;

type Snapshot = {
  busy: boolean;
  byStep: ByInstr;
  // The one call's reasoning and its reply as it came.
  reasoning: string;
  raw: string;
  error: string | null;
};

const key = (docId: string) => `notice.stepInstructions.${docId}`;
// New names: these were one per step, keyed by category, and are one string now.
const reasonKey = (docId: string) => `notice.stepTextReasoning.${docId}`;
const rawKey = (docId: string) => `notice.stepTextRaw.${docId}`;

const store = createDocEntryStore<Snapshot>((docId) => ({
  snapshot: {
    busy: false,
    byStep: readState<ByInstr>(docId, key(docId), {}),
    reasoning: readState<string>(docId, reasonKey(docId), ''),
    raw: readState<string>(docId, rawKey(docId), ''),
    error: null,
  },
}));

const update = store.patch;

// WHOLE PAGES, at a width text can be read at. The step's band is cut where the next step's
// segment begins — a boundary drawn around diagrams, which has been seen to clip a step's
// own last printed line mid-sentence — and a transcription that silently loses a language is
// exactly the failure this call exists to stop. The prompt says which steps each page carries.
const PAGE_WIDTH = 1500;

// Every page any step is printed on, once each, in order.
const pagesOf = (steps: StepBand[]): number[] =>
  [...new Set(steps.flatMap((s) => s.pages))].sort((a, b) => a - b);

const errorOf = (e: unknown, t: (k: string) => string): string =>
  (axios.isAxiosError(e) && (e.response?.data as { error?: string })?.error)
  || (e instanceof Error && e.message)
  || t('notice.err.generic');

export async function readStepInstructions(
  docId: string,
  file: Blob,
  steps: StepBand[],
  model: string,
  t: (k: string) => string,
) {
  if (store.get(docId).busy) return;
  if (!model || steps.length === 0) {
    update(docId, { error: t('notice.cat2.noRun') });
    return;
  }
  const halted = haltGate(docId);
  update(docId, { busy: true, error: null, byStep: {}, reasoning: '', raw: '' });
  writeState(docId, key(docId), {});
  try {
    const images = await withDoc(file, async (doc) => {
      const out: { page: number; image: string }[] = [];
      for (const page of pagesOf(steps)) {
        if (halted()) break;
        // top 0, bottom 1: the whole page, whatever band the classification drew on it.
        const image = await renderPdfPageBand(doc, page, 0, 1, PAGE_WIDTH);
        if (image) out.push({ page, image });
      }
      return out;
    }, []);
    if (halted() || !images.length) return;

    const { data } = await axios.post<{
      steps?: { step: number; instructions: InstrEntry[] }[];
      reasoning?: string;
      raw?: string;
    }>('/api/notice/step-instructions', {
      model, images, prompt: buildStepInstructionsPrompt(steps),
    });

    // Keyed by category, like everything downstream: a step number that names no step of this
    // manual is dropped rather than filed under a category nothing reads.
    const byNum = new Map(steps.map((s) => [s.num, s.category]));
    const byStep: ByInstr = {};
    for (const one of data.steps ?? []) {
      const category = byNum.get(one.step);
      if (category) byStep[category] = one.instructions ?? [];
    }
    writeState(docId, key(docId), byStep);
    writeState(docId, reasonKey(docId), data.reasoning || '');
    writeState(docId, rawKey(docId), data.raw || '');
    update(docId, { byStep, reasoning: data.reasoning || '', raw: data.raw || '' });
  } catch (e) {
    // A call Stop cancelled is not a failure: the table keeps what it had.
    if (!isStopped(e)) update(docId, { error: errorOf(e, t) });
  } finally {
    update(docId, { busy: false });
  }
}

// THE PAGES THE CALL CARRIES. Whole pages, at PAGE_WIDTH, exactly as the run renders them — the
// point of this call is that it reads a page and not a band.
export async function stepInstructionsPictures(
  file: Blob,
  steps: StepBand[],
  t: (k: string) => string,
): Promise<TipPicture[]> {
  return withDoc(file, async (doc) => {
    const out: TipPicture[] = [];
    for (const page of pagesOf(steps)) {
      const image = await renderPdfPageBand(doc, page, 0, 1, PAGE_WIDTH);
      if (image) {
        out.push(asPicture(
          image,
          t('notice.parts.promptStepPage').replace('{n}', String(page)),
          `${t('notice.pdf.page')} ${page}`,
        ));
      }
    }
    return out;
  }, []);
}

export const useStepInstructions = store.useSnapshot;

// What a step prints, outside a component — for the readers downstream that have a category
// and no hook to call.
export const getStepInstructions = (docId: string): ByInstr => store.get(docId).byStep;
